import {FC, useState} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {isNotEmpty} from '../../../../../../_metronic/helpers'
import {useListView} from '../../../../../../_metronic/common/table/core/ListViewProvider'
import {useQueryResponse} from '../../../../../../_metronic/common/table/core/QueryResponseProvider'
import {ListLoading} from '../../../../../../_metronic/common/table/loading/ListLoading'
import {EditFormInput} from '../../../../../../_metronic/common/edit-modal/EditFormInput'
import {useAuth} from '../../../../auth'
import {createStockCategory, updateStockCategory} from '../core/_requests'
import {StockCategory} from '../core/_models'

type Props = {
  isCategoryLoading: boolean
  category: StockCategory
}

const editCategorySchema = Yup.object().shape({
  name: Yup.string().min(1, 'Name is required').max(50, 'Maximum 50 symbols').required('Name is required'),
})

export const StockCategoryEditModalForm: FC<Props> = ({isCategoryLoading, category}) => {
  const {setItemIdForUpdate} = useListView()
  const {refetch} = useQueryResponse()
  const {currentUser} = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [categoryForEdit] = useState<StockCategory>({...category})

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({
    initialValues: categoryForEdit,
    validationSchema: editCategorySchema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.id)) {
          await updateStockCategory(currentUser, values)
        } else {
          await createStockCategory(currentUser, values)
        }
        setError(null)
        cancel(true)
      } catch (ex: any) {
        console.error(ex)
        setError(ex.response?.data?.message || ex.message || 'Unable to save category')
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <>
      {error && <div className='alert alert-danger'>{error}</div>}
      <form id='kt_modal_add_category_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        <EditFormInput label='Name' name='name' fieldProps={formik.getFieldProps('name')} touchedProp={formik.touched.name} errorsProp={formik.errors.name} isSubmitting={formik.isSubmitting} isLoading={isCategoryLoading} />
        <EditFormInput label='Description' name='description' fieldProps={formik.getFieldProps('description')} touchedProp={formik.touched.description} errorsProp={formik.errors.description} isSubmitting={formik.isSubmitting} isLoading={isCategoryLoading} isRequired={false} />

        <div className='text-center pt-15'>
          <button type='reset' onClick={() => cancel()} className='btn btn-light me-3' disabled={formik.isSubmitting || isCategoryLoading}>
            Discard
          </button>
          <button type='submit' className='btn btn-primary' disabled={isCategoryLoading || formik.isSubmitting || !(formik.isValid && formik.dirty)}>
            <span className='indicator-label'>Submit</span>
            {(formik.isSubmitting || isCategoryLoading) && (
              <span className='indicator-progress'>
                Please wait... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {(formik.isSubmitting || isCategoryLoading) && <ListLoading />}
    </>
  )
}
